import { useEffect, useRef } from 'react';
import { getPersonalMemberCardParamsSchema } from '@zlinkx/shared-v2';
import { useAuth } from '@/features/auth/context/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCreatePersonalMemberCard,
  useGetPersonalMemberCard,
  useUpdatePersonalMemberCard,
} from '@/generated/hooks';
import { queryKeys } from '@/generated/hooks/query-keys';
import type {
  UsePersonalMemberCardOptions,
  PersonalCardDraft,
} from '@/types/personal-member-card.types';

export function usePersonalMemberCard(options: UsePersonalMemberCardOptions) {
  const { merchantId, userId, enabled = true } = options;
  const queryClient = useQueryClient();
  const { user, loading } = useAuth();
  const identity = useRef(user?.id);
  identity.current = user?.id;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const validTarget = getPersonalMemberCardParamsSchema.safeParse({
    merchantId,
    userId,
  }).success;
  const query = useGetPersonalMemberCard(
    { merchantId, userId: userId ?? '' },
    {
      enabled: enabled && validTarget && !loading && user !== null,
      gcTime: 0,
      staleTime: 0,
      refetchOnMount: 'always',
      retry: false,
    },
  );
  const createMutation = useCreatePersonalMemberCard();
  const updateMutation = useUpdatePersonalMemberCard();
  const save = async (draft: PersonalCardDraft) => {
    if (!validTarget || userId === null || user === null || loading)
      throw new Error('Invalid card context');
    const startedBy = user.id;
    const params = { merchantId, userId };
    const saved =
      draft.expectedRevision === null
        ? await createMutation.mutateAsync({
            ...params,
            data: { member_id: draft.memberId },
          })
        : await updateMutation.mutateAsync({
            ...params,
            data: {
              member_id: draft.memberId,
              expected_revision: draft.expectedRevision,
            },
          });
    if (!mounted.current || identity.current !== startedBy)
      throw new Error('Card session changed');
    queryClient.setQueryData(
      queryKeys.merchantmembers.getPersonalMemberCard(merchantId, userId),
      saved,
    );
    return saved;
  };
  return {
    card:
      !loading &&
      user !== null &&
      query.error === null &&
      query.isFetchedAfterMount
        ? query.data
        : undefined,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    isSaving: createMutation.isPending || updateMutation.isPending,
    save,
  };
}
